import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import OptimizedImage from './OptimizedImage';

interface GalleryProps {
  className?: string;
}

type TransitionEffect = 'fade' | 'dissolve' | 'slide';

type MediaItem = {
  url: string;
  type: 'image' | 'video';
};

const Gallery = ({ className = "" }: GalleryProps) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionEffect, setTransitionEffect] = useState<TransitionEffect>('fade');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        console.log('Gallery: Fetching media from gallery_images bucket...');
        const { data: files, error } = await supabase
          .storage
          .from('gallery_images')
          .list();

        if (error) {
          console.error('Gallery: Error fetching media:', error);
          setError('Failed to load gallery media');
          return;
        }

        if (!files || files.length === 0) {
          console.log('Gallery: No media found in bucket');
          setError('No media available in the gallery');
          return;
        }

        console.log('Gallery: Found files:', files);

        const mediaFiles = files
          .filter(file => {
            const isImage = file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const isVideo = file.name.match(/\.(mp4|webm|mov)$/i);
            return isImage || isVideo;
          })
          .map(file => {
            const url = `${supabase.storage.from('gallery_images').getPublicUrl(file.name).data.publicUrl}`;
            const type = file.name.match(/\.(mp4|webm|mov)$/i) ? 'video' : 'image';
            console.log(`Gallery: Generated URL for ${file.name}:`, url);
            return { url, type };
          });

        console.log('Gallery: Final media URLs:', mediaFiles);
        setMediaItems(mediaFiles);
      } catch (err) {
        console.error('Gallery: Unexpected error:', err);
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, []);

  useEffect(() => {
    if (mediaItems.length === 0) return;

    const effects: TransitionEffect[] = ['fade', 'dissolve', 'slide'];
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % mediaItems.length);
      setTransitionEffect(effects[Math.floor(Math.random() * effects.length)]);
    }, 7000);

    return () => clearInterval(timer);
  }, [mediaItems.length]);

  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 w-full h-[600px] rounded-lg"></div>;
  }

  if (error) {
    return <div className="text-gray-500 text-center p-8">{error}</div>;
  }

  if (mediaItems.length === 0) {
    return <div className="text-gray-500 text-center p-8">No media found in gallery</div>;
  }

  const getTransitionClasses = (index: number): string => {
    const isActive = index === currentIndex;
    const baseClasses = "absolute inset-0 transition-all duration-1000";
    
    switch (transitionEffect) {
      case 'fade':
        return `${baseClasses} ${isActive ? 'opacity-100' : 'opacity-0'}`;
      case 'dissolve':
        return `${baseClasses} ${isActive ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`;
      case 'slide':
        return `${baseClasses} transform ${
          isActive ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`;
      default:
        return `${baseClasses} ${isActive ? 'opacity-100' : 'opacity-0'}`;
    }
  };

  return (
    <div className={`relative w-full h-[600px] overflow-hidden rounded-lg ${className}`}>
      {mediaItems.map((item, index) => (
        <div
          key={item.url}
          className={getTransitionClasses(index)}
        >
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            {item.type === 'video' ? (
              <video
                src={item.url}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay={index === currentIndex}
                muted
                loop
                playsInline
              />
            ) : (
              <OptimizedImage
                src={item.url}
                alt={`Gallery item ${index + 1}`}
                className="max-w-full max-h-full object-contain"
                width={1920}
                height={1080}
                priority={index === currentIndex}
              />
            )}
          </div>
        </div>
      ))}
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {mediaItems.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? "bg-white w-4"
                : "bg-white/50 hover:bg-white/75"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Gallery;